import React, { useEffect, useState } from "react";
import {
  Modal,
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CustomSelect } from "../../CustomControls/CustomSelect";
import { Spinner } from "../../Utils/Loaders";
import {
  getCreateRegulatoryEvents,
  getRegulatoryEventAttachments,
} from "../../../api/clinicalTrial";
import { attachmentEntityType } from "../../../variables/Enums";
import { ToastContainer } from "react-toastify";

export const ModalEditRegulatoryEvent = ({
  show,
  protocolId,
  regulatorySubmissionId,
  regulatorySubmissionAttachments,
  currentMedicalRecordNumber,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    categories: [],
    groups: [],
    protocolAttachments: [],
    patient: undefined,
  });

  const [categoryOptions, setCategoryOptions] = useState();
  const [groupOptions, setGroupOptions] = useState();
  const [protocolAttachmentOptions, setProtocolAttachmentOptions] = useState();
  const [patientOptions, setPatientOptions] = useState();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    var loadData = async () => {
      const result = await getCreateRegulatoryEvents(protocolId);

      if (result.status === 200) {
        const categories = result.data.attachmentCategories;

        const groupedCategories = [
          {
            label: t("regulatorySubmissions.createEditModal.startupCategory"),
            options: categories
              .filter((x) => x.entityTypeId === attachmentEntityType.Startup)
              .map((x) => ({ id: x.id, name: x.name })),
          },
          {
            label: t(
              "regulatorySubmissions.createEditModal.practitionersCategory"
            ),
            options: categories
              .filter(
                (x) => x.entityTypeId === attachmentEntityType.Practitioner
              )
              .map((x) => ({
                id: x.id,
                name: `${x.practitionerName ? x.practitionerName + " - " : ""}${
                  x.name
                }`,
                practitionerId: x.practitionerId,
              })),
          },
          {
            label: t("regulatorySubmissions.createEditModal.hospitalCategory"),
            options: categories
              .filter((x) => x.entityTypeId === attachmentEntityType.Hospital)
              .map((x) => ({ id: x.id, name: x.name })),
          },
          {
            label: t("regulatorySubmissions.createEditModal.protocolCategory"),
            options: categories
              .filter((x) => x.entityTypeId === attachmentEntityType.Protocols)
              .map((x) => ({
                id: x.id,
                name: `${x.medicalRecordNumber}${x.name}`,
                medicalRecordNumber: x.medicalRecordNumber,
              })),
          },
          {
            label: t(
              "regulatorySubmissions.createEditModal.regulatorySubmissionCategory"
            ),
            options: categories
              .filter(
                (x) =>
                  x.entityTypeId === attachmentEntityType.RegulatorySubmissions
              )
              .map((x) => ({
                id: x.id,
                name: x.name,
              })),
          },
        ];

        const groups = result.data.groups.map((x) => ({
          id: x.id,
          name: x.name,
        }));

        const patients = result.data.patients.map((x) => ({
          id: x.medicalRecordNumber,
          name: x.numberInProtocol || x.medicalRecordNumber,
          numberInProtocol: x.numberInProtocol || null,
        }));

        const protocolAttachments = result.data.protocolAttachments.map(
          (x) => ({
            id: x.id,
            name: x.name,
            medicalRecordNumber: x.medicalRecordNumber,
          })
        );

        const withoutPatient = protocolAttachments
          .filter((x) => !x.medicalRecordNumber)
          .map((x) => ({ id: x.id, name: x.name }));

        const attachmentsWithPatient = protocolAttachments.filter(
          (x) => x.medicalRecordNumber
        );

        const groupedByPatient = attachmentsWithPatient.reduce((acc, item) => {
          if (!acc[item.medicalRecordNumber]) {
            acc[item.medicalRecordNumber] = [];
          }
          acc[item.medicalRecordNumber].push({ id: item.id, name: item.id });
          return acc;
        }, {});

        const groupedProtocolAttachments = [
          {
            label: t(
              "regulatorySubmissions.createEditModal.protocolAttachments"
            ),
            options: withoutPatient,
          },
          ...Object.entries(groupedByPatient).map(
            ([medicalRecordNumber, options]) => ({
              label: medicalRecordNumber,
              options,
            })
          ),
        ];

        setCategoryOptions(groupedCategories);
        setGroupOptions(groups);
        setProtocolAttachmentOptions(groupedProtocolAttachments);
        setPatientOptions(patients);

        // ---- PRESELECCIONES A PARTIR DE selectedAttachments ----
        const catFlat = flattenOptions(groupedCategories);
        const protFlat = flattenOptions(groupedProtocolAttachments);

        // groups (únicos por id)
        const selectedGroups = Array.from(
          new Map(
            (regulatorySubmissionAttachments ?? [])
              .filter((a) => a.groupId != null)
              .map((a) => [a.groupId, { id: a.groupId, name: a.groupName }])
          ).values()
        );

        // categories (match por id + practitionerId/medicalRecordNumber cuando existan)
        const selectedCategories = [];
        const seenCat = new Set();

        (regulatorySubmissionAttachments ?? [])
          .filter((a) => a.attachmentCategoryId != null && a.groupId === null)
          .forEach((att) => {
            const opt = catFlat.find(
              (o) =>
                o.id === att.attachmentCategoryId &&
                (o.practitionerId ?? null) === (att.practitionerId ?? null) &&
                (o.medicalRecordNumber ?? null) ===
                  (att.medicalRecordNumber ?? null)
            );
            if (opt) {
              const k = `${opt.id}|${opt.practitionerId ?? ""}|${
                opt.medicalRecordNumber ?? ""
              }`;
              if (!seenCat.has(k)) {
                selectedCategories.push(opt);
                seenCat.add(k);
              }
            }
          });

        // protocol attachments (los adjuntos del estado sin categoría y con entityType Protocols)
        const selectedProtocolAttachments = [];
        const seenProt = new Set();

        (regulatorySubmissionAttachments ?? [])
          .filter(
            (a) =>
              !a.attachmentCategoryId &&
              a.attachmentEntityTypeId === attachmentEntityType.Protocols &&
              a.groupId === null
          )
          .forEach((att) => {
            const opt = protFlat.find((o) => o.id === att.id);
            if (opt && !seenProt.has(opt.id)) {
              selectedProtocolAttachments.push(opt);
              seenProt.add(opt.id);
            }
          });

        // Preseleccionar paciente actual
        const selectedPatient = currentMedicalRecordNumber
          ? patients.find((p) => p.id === currentMedicalRecordNumber)
          : undefined;

        setFormData({
          categories: selectedCategories, // array de opciones
          groups: selectedGroups, // array de {id,name}
          protocolAttachments: selectedProtocolAttachments, // array de opciones
          patient: selectedPatient,
        });
      }
    };

    loadData();
    setFormData({
      categories: [],
      groups: [],
      protocolAttachments: [],
      patient: undefined,
    });
  }, []);

  const flattenOptions = (groups) => {
    const out = [];
    if (!groups) return out;
    for (let i = 0; i < groups.length; i++) {
      const opts = groups[i] && groups[i].options ? groups[i].options : [];
      for (let j = 0; j < opts.length; j++) out.push(opts[j]);
    }
    return out;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const { categories, groups, protocolAttachments, patient } = formData;

    const payload = {
      groups: groups.map((g) => g.id),
      protocolAttachments: protocolAttachments.map((a) => ({
        id: a.id,
        medicalRecordNumber: a.medicalRecordNumber,
      })),
      attachmentsCategories: categories.map((cat) => ({
        id: cat.id,
        practitionerId: cat.practitionerId || null,
      })),
      medicalRecordNumber: patient ? patient.id : null,
      numberInProtocol: patient?.numberInProtocol ?? null,
    };

    try {
      setSaving(true);
      const result = await getRegulatoryEventAttachments(
        regulatorySubmissionId,
        payload
      );

      if (result.status === 200) {
        onSave(
          result.data.attachments,
          patient ? patient.id : null,
          patient?.numberInProtocol ?? null
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      dialogClassName="rounded-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("regulatorySubmissions.createEditModal.editRegulatory")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!categoryOptions && <Spinner />}
        {categoryOptions && (
          <>
            <FormGroup controlId="formPatients">
              <ControlLabel>
                {t("regulatorySubmissions.createEditModal.patientSelect")}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.patient || ""}
                name="patient"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={false}
                isClearable={true}
                options={patientOptions || []}
                closeMenuOnSelect={true}
                getOptionLabel={(option) => option.name}
              />
            </FormGroup>
            <FormGroup controlId="formGroups">
              <ControlLabel>
                {t("regulatorySubmissions.createEditModal.groupsSelect")}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.groups || ""}
                name="groups"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                options={groupOptions || []}
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
            <FormGroup controlId="formCategories">
              <ControlLabel>
                {t("regulatorySubmissions.createEditModal.categoriesSelect")}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.categories || ""}
                name="categories"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                closeMenuOnSelect={false}
                options={categoryOptions || []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
            <FormGroup controlId="formProtocolAttachments">
              <ControlLabel>
                {t(
                  "regulatorySubmissions.createEditModal.protocolAttachmentsSelect"
                )}
              </ControlLabel>
              <CustomSelect
                isDisabled={saving}
                value={formData.protocolAttachments}
                name="protocolAttachments"
                placeholder={t("commons.select")}
                onChange={handleChange}
                isMulti={true}
                isClearable={true}
                closeMenuOnSelect={false}
                options={protocolAttachmentOptions || []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </FormGroup>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          {t("regulatorySubmissions.createEditModal.cancelLabel")}
        </Button>
        {saving && <Spinner />}
        {!saving && (
          <Button variant="btn btn-primary" onClick={handleSubmit}>
            {t("regulatorySubmissions.createEditModal.acceptLabel")}
          </Button>
        )}
      </Modal.Footer>
      <ToastContainer position="top-right" pauseOnHover />
    </Modal>
  );
};
